import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { renderPinLabel } from './pin-label.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderer del cuerpo de un transistor TO-126. Mismo enfoque de
 * relieve que el DIP (rim + 4 chaflanes + cara superior interior),
 * más un TALADRO circular blanco prominente en la mitad superior
 * (equivalente al agujero de montaje visible en la referencia).
 * Cuando el componente está invertido, se muestra el cuerpo plano
 * (sin chaflanes ni taladro visible).
 */
export function renderTo126Body(
  def: ComponentDef,
  labelText: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
  pinLabels: string[] = [],
): Konva.Group {
  const g = new Konva.Group();

  const width = def.body.length_mm ?? 7.62;
  const height = def.body.width_mm ?? 15.24;
  const cornerRadius = Math.max(
    def.body.corner_radius_mm ?? 0.6,
    Math.min(width, height) * 0.06,
  );

  const bodyColor = def.appearance.body_color;
  const light = mix(bodyColor, '#ffffff', 0.28);
  const veryLight = mix(bodyColor, '#ffffff', 0.55);
  const dark = mix(bodyColor, '#000000', 0.45);
  const veryDark = mix(bodyColor, '#000000', 0.65);
  const rimColor = mix(bodyColor, '#ffffff', 0.05);
  const outlineColor = '#000000';
  const outlineWidth = 0.12;

  // Grupo con clip a la silueta redondeada.
  const bodyGroup = new Konva.Group({
    clipFunc: (ctx) => {
      const x = -width / 2, y = -height / 2, w = width, h = height;
      const r = Math.min(cornerRadius, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    },
  });

  // Base rim (dentro del clip). Sin stroke: el contorno va después.
  const rect = new Konva.Rect({
    x: -width / 2,
    y: -height / 2,
    width,
    height,
    cornerRadius,
    fill: flipped ? dark : rimColor,
    listening: true,
  });
  rect.setAttr('nodeKind', 'body');
  bodyGroup.add(rect);

  if (!flipped) {
    const hL = width / 2;
    const hW = height / 2;
    const ch = Math.min(width, height) * 0.10;
    const iL = hL - ch;
    const iW = hW - ch;
    const bevelOuter = mix(bodyColor, '#ffffff', 0.12);
    const bodyGradStops = [0, bevelOuter, 0.5, light, 1, veryLight];

    // 4 chaflanes.
    bodyGroup.add(new Konva.Line({
      points: [-hL, -hW, hL, -hW, iL, -iW, -iL, -iW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));
    bodyGroup.add(new Konva.Line({
      points: [-hL, hW, -iL, iW, iL, iW, hL, hW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));
    bodyGroup.add(new Konva.Line({
      points: [-hL, -hW, -iL, -iW, -iL, iW, -hL, hW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));
    bodyGroup.add(new Konva.Line({
      points: [hL, -hW, hL, hW, iL, iW, iL, -iW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));

    // Cara superior interior (rectángulo): tono oscuro con degradado.
    bodyGroup.add(new Konva.Rect({
      x: -iL,
      y: -iW,
      width: 2 * iL,
      height: 2 * iW,
      cornerRadius: cornerRadius * 0.5,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: [
        0, mix(bodyColor, '#000000', 0.55),
        0.5, mix(bodyColor, '#000000', 0.35),
        1, mix(bodyColor, '#000000', 0.1),
      ],
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      listening: false,
    }));

    // Marca de molde pequeña (esquina superior derecha, como en la
    // referencia). Solo cuando cabe con margen.
    const holeRForMark = Math.min(width, height) * 0.22;
    const markR = Math.min(width, height) * 0.07;
    const markX = iL - markR - 0.35;
    const markY = -iW + markR + 0.35;
    if (markX > holeRForMark + 0.5) {
      bodyGroup.add(new Konva.Circle({
        x: markX,
        y: markY,
        radius: markR,
        fillRadialGradientStartPoint: { x: -markR * 0.3, y: -markR * 0.3 },
        fillRadialGradientEndPoint: { x: 0, y: 0 },
        fillRadialGradientStartRadius: 0,
        fillRadialGradientEndRadius: markR,
        fillRadialGradientColorStops: [0, dark, 1, veryDark],
        stroke: veryDark,
        strokeWidth: 0.05,
        listening: false,
      }));
    }
  }

  // Taladro de montaje: círculo blanco prominente en la mitad superior
  // del cuerpo, con un anillo oscuro que le da profundidad de "agujero
  // pasante". Se dibuja SIEMPRE — invertido el cuerpo se ve plano y
  // oscuro (es la cara de la pestaña metálica), pero el taladro es un
  // agujero que atraviesa la pieza y por tanto sigue viéndose.
  {
    const hW = height / 2;
    const holeR = Math.min(width, height) * 0.22;
    const holeCy = -hW + Math.min(width, height) * 0.42;
    // Anillo exterior (sombra hundida alrededor del taladro).
    bodyGroup.add(new Konva.Circle({
      x: 0,
      y: holeCy,
      radius: holeR + 0.35,
      fillRadialGradientStartPoint: { x: 0, y: 0 },
      fillRadialGradientEndPoint: { x: 0, y: 0 },
      fillRadialGradientStartRadius: holeR,
      fillRadialGradientEndRadius: holeR + 0.35,
      fillRadialGradientColorStops: [0, veryDark, 1, mix(bodyColor, '#000000', 0.4)],
      listening: false,
    }));
    // Cara interior del taladro (blanco pasante).
    bodyGroup.add(new Konva.Circle({
      x: 0,
      y: holeCy,
      radius: holeR,
      fillRadialGradientStartPoint: { x: -holeR * 0.25, y: -holeR * 0.25 },
      fillRadialGradientEndPoint: { x: 0, y: 0 },
      fillRadialGradientStartRadius: 0,
      fillRadialGradientEndRadius: holeR,
      fillRadialGradientColorStops: [0, '#ffffff', 1, '#dcdcdc'],
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      listening: false,
    }));
  }

  g.add(bodyGroup);

  // Contorno perimetral (fuera del clip).
  g.add(new Konva.Rect({
    x: -width / 2,
    y: -height / 2,
    width,
    height,
    cornerRadius,
    stroke: outlineColor,
    strokeWidth: outlineWidth,
    listening: false,
  }));

  // Etiqueta central (valor / part number). Se coloca debajo del
  // taladro, en la mitad inferior del cuerpo.
  const ps = flipped ? -1 : 1;
  const labelGroup = new Konva.Group({
    y: height * 0.22, // mitad inferior, dejando aire con el taladro
    rotation: ps * (0 - rotationDeg),
    scaleY: ps,
    listening: false,
  });
  const fontSize = def.appearance.label?.font_size_mm ?? Math.min(width, height) * 0.22;
  const boxSize = Math.max(width, height) * 0.8;
  addUnifiedValueLabel(labelGroup, labelText, fontSize, boxSize);
  g.add(labelGroup);

  // Etiquetas por pin (junto a la base).
  const pinFont = Math.min(width * 0.16, 1.3);
  for (let i = 0; i < def.pins.length; i++) {
    const label = pinLabels[i] ?? '';
    if (label) {
      g.add(renderPinLabel(def.pins[i]!, label, 1.5, rotationDeg, flipped, pinFont));
    }
  }

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return `#${toHex(ca.r + (cb.r - ca.r) * t)}${toHex(ca.g + (cb.g - ca.g) * t)}${toHex(ca.b + (cb.b - ca.b) * t)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}
